import { Tirage as TirageApi } from "@/api/tirages/tirage.api";
import { LocaleDict, multilingualFieldSchema, SupportedLocale } from "@/i18n";
import { z } from "zod";
import { DimensionsDTO, LegacyMoneyAmount, MoneyAmount } from "./shared-types";
import { Overwrite } from "./utils";

// Re-export Tirage with attachments property for use in complex link types
export interface Tirage extends TirageApi {
  attachments?: Attachment[];
}

export interface ArtworkBasic {
  id: string;
  instanceId: string;
  oeuvresPublicationStatus: string;
  oeuvresDescriptionDimensionsOeuvre: string;
  oeuvresDescriptionDimensionsOeuvreRaw: string;
  oeuvresDescriptionDimensionsHorsMarges: string;
  sources: string;
  oeuvresDescriptionNumeroEnregistrement: string;
  oeuvresDescriptionNomArtisteAttribueA: boolean;
  oeuvresDescriptionNomArtisteAnonyme: boolean;
  oeuvresDescriptionNomArtisteDansLeGoutDe: boolean;
  oeuvresDescriptionNomArtisteEntourageDe: boolean;
  oeuvresDescriptionNomArtisteDApres: boolean;
  oeuvresDescriptionNomArtisteAtelierDe: boolean;
  oeuvresDescriptionTitre: string;
  oeuvresDescriptionTitreSansTitre: boolean;
  oeuvresDescriptionTitreInconnu: boolean;
  oeuvresDescriptionDescriptionLongue: string;
  oeuvresDescriptionSousTitre: string;
  oeuvresDescriptionAutreTitre: string;
  oeuvresDescriptionReference: string;
  oeuvresDescriptionDateDeCreation: string;
  oeuvresDescriptionDateDeCreationInconnue: boolean;
  oeuvresDescriptionPeriodeCreation: string;
  oeuvresDescriptionAireGeographique: string;
  oeuvresDescriptionEcoleMouvement: string;
  oeuvresDescriptionCategorie: string;
  oeuvresDescriptionSerie: string;
  oeuvresDescriptionTheme: string;
  oeuvresDescriptionLieuDeCreation: string;
  oeuvresDescriptionDimensionsOeuvreVerifiees: boolean;
  oeuvresdescriptiondimensionsprofondeur: boolean | null;
  oeuvresDescriptionDimensionsOeuvreInconnues: boolean;
  oeuvresDescriptionDimensionsOeuvreDouteuses: boolean;
  oeuvresDescriptionDimensionsHorsMargesVerifiees: boolean;
  oeuvresDescriptionDimensionsHorsMargesInconnues: boolean;
  oeuvresDescriptionDimensionsHorsMargesDouteuses: boolean;
  oeuvresDescriptionPoidsOeuvre: string;
  oeuvresDescriptionOeuvreDoubleFace: boolean;
  oeuvresDescriptionTypeOeuvreMultilingual: LocaleDict<string>;
  oeuvresStatus: string;
  oeuvresDescriptionTechniqueFRA: string;
  oeuvresDescriptionTechniqueGB: string;

  oeuvresDescriptionTechnique: LocaleDict<string>;

  oeuvresDescriptionSupportFRA: string;
  oeuvresDescriptionSupportGB: string;

  oeuvresDescriptionSupport: LocaleDict<string>;

  oeuvresDescriptionRemarqueSupportFRA: string;
  oeuvresDescriptionRemarqueSupportGB: string;

  oeuvresDescriptionRealisationFabrication: string;
  oeuvresDescriptionSignatureTranscription: string;

  oeuvresDescriptionSignatureLocalisationFRA: string;
  oeuvresDescriptionSignatureLocalisationGB: string;

  oeuvresDescriptionSignatureLocalisation: LocaleDict<string>;

  oeuvresDescriptionInscriptionsDos: string;
  oeuvresDescriptionInscriptionsDosEN: string;

  oeuvresDescriptionInscriptionsDosMultilingual: LocaleDict<string>;
  oeuvresDescriptionInscriptionsDosPublished: LocaleDict<boolean>;

  oeuvresDescriptionResume: string;
  oeuvresDescriptionLegendeDocumentAnnexe: string;
  oeuvresDescriptionNomFichierNumerique: string;
  oeuvresDescriptionNumeroDInventaire: string;
  oeuvresDescriptionReferenceCollectionActuelle: string;
  oeuvresDescriptionNumeroAutre: string;
  oeuvresDescriptionChampParametrableUtilisateur: string;
  oeuvresDescriptionAjoutDeMotsCles: string;
  oeuvresValidationSystemeDeNotationDeLoeuvre: number | null;
  oeuvresValidationSystemeDePublicationDeLaFicheOnlineWebFront: string;
  oeuvresValidationDescriptionOk: boolean;
  oeuvresValidationHistoriqueCollectionsOk: boolean;
  oeuvresValidationExpositionsOk: boolean;
  oeuvresValidationBibliographieOk: boolean;
  oeuvresValidationVentesOk: boolean;
  oeuvresValidationSelectionnerFiche: boolean;
  oeuvresValidationSource: string;
  oeuvresValidationOeuvreDocumentaire: boolean;
  oeuvresValidationOeuvreImportante: boolean;
  oeuvresValidationOeuvreMajeure: boolean;
  oeuvresValidationAttributionDouteuse: boolean;
  oeuvresValidationFaux: boolean;
  oeuvresValidationLost: boolean;
  oeuvresValidationTitleNotConfirmed: boolean;
  oeuvresValidationDestroy: boolean;
  oeuvresDescriptionRestaurationNumerique: boolean;
  oeuvresValidationStole: boolean;
  oeuvresValidationNotesDeTravail: string;
  oeuvresValidationRemarquesPubliques: string;
  oeuvresValidationRemarquesPubliquesEN: string;
  oeuvresValidationRemarquesPubliquesMultilingual: Record<SupportedLocale, string>;
  oeuvresValidationRemarquesPersonnelles: string;
  oeuvresLocalisationHistoriqueCollectionCatalogue: string;
  oeuvresLocalisationHistoriqueCollectionNote: string;
  oeuvresLocalisationHistoriqueOeuvreNonLocalisee: boolean;
  oeuvresLocalisationHistoriqueHistoriqueNom: string;
  oeuvresLocalisationHistoriqueHistoriqueVille: string;
  oeuvresLocalisationHistoriqueHistoriqueDates: string;
  oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere: boolean;
  oeuvresLocalisationHistoriqueLieuDeStockage: string;
  oeuvresLocalisationHistoriqueNotesRangement: string;
  oeuvresLocalisationHistoriqueMarquage: boolean;
  oeuvresLocalisationHistoriqueDepotAffectionPret: boolean;
  oeuvresLocalisationHistoriqueNoteDepotAffectionPret: string;
  oeuvresLocalisationHistoriqueDateDepotAffectionPret: string;
  oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret: string;
  oeuvresLocalisationHistoriqueNotes: string;
  oeuvresRegieCadre: boolean;
  oeuvresRegieDescriptionCadre: string;
  oeuvresRegieDimensionsCadre: string;
  oeuvresRegieCaisseTransport: boolean;
  oeuvresRegieDescriptionCaisse: string;
  oeuvresRegieDimensionsCaisse: string;
  oeuvresRegiePoidsCaisse: string;
  oeuvresRegiePoidsCaisseAvecOeuvre: string;
  oeuvresRegiePhotoCaisse: string;
  oeuvresRegieNotesRegie: string;
  oeuvresRegieNotesCondition: string;
  oeuvresPhotosNotesPhotos: string;
  oeuvresPhotosPhotoScanAFaire: boolean;
  oeuvresPhotosPhotoOkWeb: boolean;
  oeuvresPhotosPhotoHdOk: boolean;
  oeuvresWaitingPublication: boolean;
  oeuvresNotPublished: boolean;
  oeuvresPhotosNotesPreparationPhotos: string;
  oeuvresCreditsCreditOeuvre: string;
  oeuvresCreditsCreditPhotoOeuvre: string;
  oeuvresCreditsNatureDroits: string;
  oeuvresCreditsMontantDroits: LegacyMoneyAmount;
  oeuvresCreditsRemarqueCreditsDroits: string;
  oeuvresValorisationEstimation: string;
  oeuvresValorisationDateEstimation: string;
  oeuvresValorisationNotesEstimation: string;
  oeuvresValorisationValeurAssurance: string;
  oeuvresValorisationDateValeurAssurance: string;
  oeuvresValorisationRemarqueValeurAssurance: string;
  oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs: string;
  oeuvresDocumentsAdministratifsDocumentEdite: boolean;
  oeuvresDocumentsAdministratifsLieuEtDateDocument: string;
  oeuvresDescriptionTechniqueConfirmer: boolean;
  oeuvresDescriptionSupportConfirmer: boolean;
  oeuvresDescriptionNewBdOk: boolean;
  oeuvresRangementRecolementMarquage: boolean;
  oeuvresRangementRecolementRack: string;
  oeuvresRangementRecolementPieceBox: string;
  oeuvresRangementRecolementRangee: string;
  oeuvresRangementRecolementMeuble: string;
  oeuvresRangementRecolementEtagere: string;
  oeuvresRangementRecolementBoiteCarton: string;
  oeuvresRangementRecolementClasseur: string;
  oeuvresRangementRecolementAutreReferenceRangement: string;
  oeuvresRangementRecolementDateRangement: string;
  oeuvresRangementRecolementDateMarquage: string;
  oeuvresRangementRecolementOperateurRecolement: string;
  oeuvresRangementRecolementDateRecolement: string;
  oeuvresRangementRecolementReferenceCampagneRecolement: string;
  oeuvresRangementRecolementAutreReferenceRecolement: string;
  oeuvresRangementRecolementRecolementOk: boolean;
  oeuvresAdminDocLettreInclusionEdit: boolean;
  oeuvresAdminDocCertificatExist: boolean;
  oeuvresRangementRecolementNotesRecolement: string;
  oeuvresAdministratifNotes: string;
  oeuvresRestored: boolean;
  oeuvresToRestore: boolean;

  oeuvreAchatTitre: string;
  oeuvreAchatDate: string;
  oeuvreAchatMontant?: MoneyAmount;
  oeuvreAchatMontantTVA?: MoneyAmount;
  oeuvreAchatMontantDroitsDouane?: MoneyAmount;
  oeuvreAchatMontantDroitsDeSuite?: MoneyAmount;
  oeuvreAchatMontantFraisDeVente?: MoneyAmount;

  oeuvreVenteTitre: string;
  oeuvreVenteDate: string;
  oeuvreVenteMontant?: MoneyAmount;
  oeuvreVenteMontantTVA?: MoneyAmount;
  oeuvreVenteMontantTop?: MoneyAmount;

  oeuvresPhotoProjectDescription: string;
  oeuvresPhotoPeopleAndModelNames: string;
  createdAt: Date;
  updatedAt: Date | null;
}

interface RelationArtwork {
  selectionPageId: string | null;
  instanceId: string;
  relatedExhibitions: any[];
  relatedArtwork: Artwork[];
  relatedAudiovisual: Audiovisual[];
  relatedDocument: Document[];
  relatedWriting: Writing[];
  relatedPhotography: Photography[];
  relatedMultipleprint: Multipleprint[];
  relatedAuthor: Author[];
  artworkInExhibitions: ArtworkInExhibition[];
  artworkInAuctions: ArtworkInAuction[];
  artworkInBibliography: ArtworkInBibliography[];
  artworkHistory: ArtworkHistory[];
  relatedEnsemble: Ensemble[];
  attachments: Attachment[];
}

export interface FetchedArtwork extends ArtworkBasic, RelationArtwork {}

export type Artwork = Overwrite<
  FetchedArtwork,
  {
    oeuvresDescriptionDimensionsOeuvre?: DimensionsDTO;
    oeuvresRegieDimensionsCadre?: DimensionsDTO;
    oeuvresRegieDimensionsCaisse?: DimensionsDTO;
    oeuvresDescriptionDimensionsHorsMarges?: DimensionsDTO;
  }
>;

export interface ArtworkForm extends ArtworkBasic {
  oeuvresDescriptionNomArtisteChoix: string;
  oeuvresDescriptionTitreChoix: string;
}

export type TinyArtwork = Pick<
  Artwork,
  "id" | "attachments" | "oeuvresDescriptionTitre"
>;

export interface SubmittedAuthenticationForm {
  id: string;
  receptionDate: Date;
  sender: string;
  type: string;
  status: string;
  isPaid?: boolean;
  images?: Array<{
    id: string;
    title: string;
    weight: number;
    downloadUrl?: string;
  }>;
  instance: {
    id: string
  };
  documents?: Array<{
    id: string;
    title: string;
    weight: number;
    downloadUrl?: string;
  }>;
  holderName?: string;
  holderEmail?: string;
  phone?: string;
  createdAt?: Date | string;
}

export interface Attachment {
  id?: string;
  name: string;
  position: string;
  docId: string;
  caption?: LocaleDict<string>;
  legend?: string;
  legendEN?: string;
  description?: LocaleDict<string>;
  descriptionFR?: string;
  descriptionEN?: string;
  textAlt?: LocaleDict<string>;
  textAltFR?: string;
  textAltEN?: string;
  details?: string | null;
  largePublicUrl?: string;
  mediumPublicUrl?: string;
  publicUrl?: string;
  order?: number;
  published: boolean;
  instanceId?: string;
  isHd?: boolean;
}

export interface FetchedMultipleprint {
  id: string;
  instanceId: string;
  estampesMultiplesDescriptionNumeroEnregistrement: string;
  estampesMultiplesDescriptionAuteurArtisteAttribueA: boolean;
  estampesMultiplesDescriptionAuteurArtisteAnonyme: boolean;
  estampesMultiplesDescriptionAuteurArtisteDApres: boolean;
  estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe: boolean;
  estampesMultiplesDescriptionAuteurArtisteEntourageDe: boolean;
  estampesMultiplesDescriptionAuteurAtelierDe: boolean;
  estampesMultiplesDescriptionTitre: string;
  estampesMultiplesDescriptionTitreSansTitre: boolean;
  estampesMultiplesDescriptionTitreInconnu: boolean;
  estampesMultiplesDescriptionTitreIncertin: boolean;
  estampesMultiplesDescriptionSousTitre: string;
  estampesMultiplesDescriptionDateDeCreation: string;
  estampesMultiplesDescriptionDateDeCreationInconnue: boolean;
  estampesMultiplesDescriptionType: string;

  estampesMultiplesDescriptionTechnique: LocaleDict<string>;

  estampesMultiplesDescriptionTechniqueFRA: string;
  estampesMultiplesDescriptionTechniqueGB: string;
  estampesMultiplesDescriptionSupportMateriau: LocaleDict<string>;
  estampesMultiplesDescriptionSupportMateriauFRA: string;
  estampesMultiplesDescriptionSupportMateriauGB: string;
  estampesMultiplesDescriptionEditeur: string;
  estampesMultiplesDescriptionFabricantImprimeur: string;
  estampesMultiplesDescriptionDimensionsProfondeur: boolean | null;
  estampesMultiplesDescriptionDimensionsOeuvre: string;
  estampesMultiplesDescriptionDimensionsOeuvreRaw: string;
  estampesMultiplesDescriptionDimensionsOeuvreVerifiees: boolean;
  estampesMultiplesDescriptionDimensionsOeuvreInconnues: boolean;
  estampesMultiplesDescriptionDimensionsOeuvreDouteuses: boolean;
  estampesMultiplesDescriptionDimensionsImage: string;
  estampesMultiplesDescriptionDimensionsImageVerifiees: boolean;
  estampesMultiplesDescriptionDimensionsImageInconnues: boolean;
  estampesMultiplesDescriptionDimensionsImageDouteuses: boolean;
  estampesMultiplesDescriptionPoidsOeuvre: string;
  estampesMultiplesDescriptionOeuvreDoubleFace: boolean;
  estampesMultiplesDescriptionDescriptionComplete: string;
  estampesMultiplesDescriptionSignatureLocalisationFR: string;
  estampesMultiplesDescriptionSignatureLocalisationGB: string;
  estampesMultiplesDescriptionSignatureLocalisation: LocaleDict<string>;
  estampesMultiplesDescriptionSignatureTranscription: string;
  estampesMultiplesDescriptionInscriptionsDosFR: string;
  estampesMultiplesDescriptionInscriptionsDosEN: string;
  estampesMultiplesDescriptionInscriptionsDos: LocaleDict<string>;
  estampesMultiplesDescriptionNotesTravail: string;
  estampesMultiplesDescriptionNotesPubliques: string;
  sources: string;
  estampesMultiplesDescriptionResume: string;
  estampesMultiplesPublicationStatus: string;
  createdAt: Date;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
  updatedBy?: unknown;
  createdBy?: unknown;

  // Relationships
  relatedArtwork: Artwork[];
  relatedAuthor: Author[];
  relatedMultiplesample: Multiplesample[];
  relatedMultipleprint: Multipleprint[];
  multipleprintInAuctions: MultipleprintInAuction[];
  multipleprintInExhibition: MultipleprintInExhibition[];
  multipleprintInBibliography: MultipleprintInBibliography[];
  relatedEnsemble: Ensemble[];
  collectorInMultipleprint: CollectorInMultipleprint[];
  multipleprintInMultipleSample: MultipleprintInMultipleSample[];
  from_Multipleprint_relatedMultipleprint: unknown[];
  attachments: Attachment[];
}

export type Multipleprint = Overwrite<
  FetchedMultipleprint,
  {
    estampesMultiplesDescriptionDimensionsOeuvre?: DimensionsDTO;
    estampesMultiplesDescriptionDimensionsImage?: DimensionsDTO;
  }
>;

export interface Multiplesample {
  id: string;
  instanceId: string;
  exemplaireDansEstampesMultiplesDescriptionNumero: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroInventaire: string;
  exemplaireDansEstampesMultiplesDescriptionLocalisation: string;
  exemplaireDansEstampesMultiplesDescriptionNotes: string;
  multipleprints: Multipleprint[];
}

export interface MultipleprintInAuction {
  id: string;
  ventesDansEstampesMultiplesDescriptionNumeroLot: string;
  ventesDansEstampesMultiplesDescriptionReproduitPage: string;
  ventesDansEstampesMultiplesDescriptionEstimationHaute: string;
  ventesDansEstampesMultiplesDescriptionEstimationBasse: string;
  ventesDansEstampesMultiplesDescriptionVendu: boolean;
  ventesDansEstampesMultiplesDescriptionResultatNonListe: boolean;
  ventesDansEstampesMultiplesDescriptionNonVendu: boolean;
  ventesDansEstampesMultiplesDescriptionNonVenu: boolean;
  ventesDansEstampesMultiplesDescriptionRetire: boolean;
  ventesDansEstampesMultiplesDescriptionMiseAPrix: string;
  ventesDansEstampesMultiplesDescriptionPrixMarteau: string;
  ventesDansEstampesMultiplesDescriptionPrixAvecFrais: string;
  ventesDansEstampesMultiplesDescriptionExemplaireVendu: string;
  multipleprint: Multipleprint;
  auction: Auction;
}

export interface Auction {
  id: string;
  instanceId: string;
  ventesDescriptionNumeroVente: string;
  ventesDescriptionCatalogue: boolean;
  ventesDescriptionTitre: string;
  ventesDescriptionDate: string;
  ventesDescriptionCommissaires: string;
  ventesDescriptionLieu: string;
  ventesDescriptionSalle: string;
  ventesDescriptionVille: string;
  ventesDescriptionPays: string;
  ventesDescriptionNotesVente: string;
  ventesDescriptionResume: string;
  sources: string;
  ventesPublicationStatus: string;
  relatedBibliography: Bibliography[];
  relatedAudiovisual: Audiovisual[];
  relatedDocument: Document[];
  relatedWriting: Writing[];
  relatedPhotography: Photography[];
  artworkInAuctions: ArtworkInAuction[];
  multipleprintInAuctions: MultipleprintInAuction[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
  attachments?: Attachment[];
}

export interface FetchedBibliography {
  id: string;
  instanceId: string;
  bibliographieDescriptionNumeroBibliographie: string;
  bibliographieDescriptionAnnee: string;
  bibliographieDescriptionType: string;
  bibliographieDescriptionIllustrations: boolean;
  bibliographieDescriptionAuteur: string;
  bibliographieDescriptionTitre: string;
  bibliographieDescriptionTitreDOrigine: string;
  bibliographieDescriptionSousTitre: string;
  bibliographieDescriptionCollection: string;
  bibliographieDescriptionEditeur: string;
  bibliographieDescriptionVille: string;
  bibliographieDescriptionPays: string;
  bibliographieDescriptionNomRevue: string;
  bibliographieDescriptionNumeroRevue: string;
  bibliographieDescriptionPageArticle: string;
  bibliographieDescriptionDatesPublication: string;
  bibliographieDescriptionResumeLong: string;
  bibliographieDescriptionResumeCourt: string;
  bibliographieDescriptionNotes: string;
  bibliographieDescriptionLocalisationReference: string;
  bibliographieDescriptionRangementReference1: string;
  bibliographieDescriptionRangementReference2: string;
  bibliographieDescriptionDimensions: string; // Assuming JSON type, adjust as needed
  bibliographieDescriptionNombrePages: string;
  bibliographieDescriptionContexture: string;
  bibliographieDescriptionCouverture: string;
  bibliographieDescriptionIsbn: string;
  bibliographieDescriptionAutreCodeBibliographie: string;
  bibliographieValidationReprosOeuvresOk: boolean;
  bibliographieValidationReprosPhotosOk: boolean;
  bibliographieValidationReprosDocumentsOk: boolean;
  bibliographieValidationListeDesOeuvresOk: boolean;
  bibliographieValidationPublicationNumerisee: boolean;
  bibliographieValidationPdfEtOcrOk: boolean;
  bibliographieValidationNoticeBibliographiqueOk: boolean;
  sources: string;
  bibliographiePublicationStatus: string;
  relatedAuthor: Author[];
  attachments: Attachment[];
  updatedAt: Date | null;
  createdAt: Date | null;
  updatedById?: string;
  createdById?: string;
}

export type Bibliography = Overwrite<
  FetchedBibliography,
  {
    bibliographieDescriptionDimensions?: DimensionsDTO;
  }
>;

export interface MultipleprintInExhibition {
  id: string;
  multipleprint: Multipleprint;
  exhibition: Exhibition;
  expositionsDansEstampesMultiplesDescriptionNumero: string;
  expositionsDansEstampesMultiplesDescriptionExemplaireExpose: string;
  attachments?: Attachment[];
}

export interface MultipleprintInBibliography {
  id: string;
  multipleprint: Multipleprint;
  bibliography: Bibliography;
  bibliographieDansEstampesMultiplesDescriptionReproduitPage: string;
  bibliographieDansEstampesMultiplesDescriptionCitePage: string;
  bibliographieDansEstampesMultiplesDescriptionPhotographe: string;
  bibliographieDansEstampesMultiplesDescriptionReproductionCoul: boolean;
  bibliographieDansEstampesMultiplesDescriptionExemplaireRepr: string;
  attachments?: Attachment[];
}

export interface CollectorInMultipleprint {
  id: string;
  collector: Collector;
  multipleprint: Multipleprint;
  estampesDansCollectionneursDescriptionInfos: string;
  attachments?: Attachment[];
}

export interface MultipleprintInMultipleSample {
  id: string;
  instanceId: string;
  multipleprint: Multipleprint;
  multiplesample: Multiplesample;
}

export interface Author {
  id: string;
  instanceId: string;
  artistesAuteursDescriptionPrenom: string;
  artistesAuteursDescriptionNom: string;
  artistesAuteursDescriptionEtatCivil: string;
  artistesAuteursDescriptionDateNaissance: string;
  artistesAuteursDescriptionVilleDeNaissance: string;
  artistesAuteursDescriptionDateDeDeces: string;
  artistesAuteursDescriptionVilleDeDeces: string;
  artistesAuteursDescriptionNationalite: string;
  artistesAuteursDescriptionResumeDates: string;
  artistesAuteursDescriptionPaysDActivite: string;
  artistesAuteursDescriptionActivite: string;
  artistesAuteursDescriptionPeriodeDactivit: string;
  artistesAuteursDescriptionNotesDeTravail: string;
  artistesAuteursDescriptionNotesBiographiques: string;
  artistesAuteursAyantDroitsNom: string;
  artistesAuteursAyantDroitsContact: string;
  artistesAuteursAyantDroitsLienAvecLAuteur: string;
  artistesAuteursAyantDroitsOrganismeGerantLesDroits: string;
  artistesAuteursExpertCrNom: string;
  artistesAuteursExpertCrContact: string;
  artistesAuteursExpertCrCatalogueRaisonne: boolean;
  artistesAuteursExpertCrEtatDuCatalogueRaisonne: string;
  artistesAuteursExpertCrDescriptionDuCatalogueRaisonne: string;
  artistesAuteursPublicationStatus: string;
  sources: string;
  createdAt: Date;
  updatedAt: Date | null;
  updatedById: string | null;
  createdById: string | null;
  relatedArtwork: Artwork[];
  relatedBibliography: Bibliography[];
  relatedMultipleprint: Multipleprint[];
}

export interface ArtworkInExhibition {
  id: string;
  artwork: Artwork;

  exhibition: Exhibition;
  oeuvresDansExpositionsDescriptionNumeroCatalogue: string;
  oeuvresDansExpositionsNotes: string;
  attachments?: Attachment[];
}

export interface ArtworkInAuction {
  id: string;
  artwork: Artwork;
  auction: Auction;
  oeuvresDansVentesDescriptionNumeroLot: string;
  oeuvresDansVentesDescriptionReproduitPage: string;
  oeuvresDansVentesDescriptionEstimationHaute: string;
  oeuvresDansVentesDescriptionEstimationBasse: string;
  oeuvresDansVentesDescriptionVendu: boolean;
  oeuvresDansVentesDescriptionResultatNonListe: boolean;
  oeuvresDansVentesDescriptionNonVendu: boolean;
  oeuvresDansVentesDescriptionNonVenu: boolean;
  oeuvresDansVentesDescriptionNonReproduit: boolean;
  oeuvresDansVentesDescriptionRetire: boolean;
  oeuvresDansVentesDescriptionMiseAPrix: string;
  oeuvresDansVentesDescriptionPrixMarteau: string;
  oeuvresDansVentesDescriptionPrixAvecFrais: string;
  oeuvresDansVentesDescriptionNotes: string;
  attachments?: Attachment[];
}

export interface ArtworkInBibliography {
  id: string;
  artwork: Artwork;
  bibliography: Bibliography;
  oeuvresDansBibliographieDescriptionReproduitPage: string;
  oeuvresDansBibliographieDescriptionCitePage: string;
  oeuvresDansBibliographieDescriptionPhotographe: string;
  oeuvresDansBibliographieDescriptionReproductionCouleur: boolean;
  oeuvresDansBibliographieDescriptionNonPagine: boolean;
  oeuvresDansBibliographieNotes: string;
  attachments?: Attachment[];
}

export interface TirageInExhibition {
  id: string;
  tirage: Tirage;
  exhibition: Exhibition;
  tirageDansExpositionsDescriptionNumeroCatalogue: string;
  tirageDansExpositionsNotes: string;
  attachments?: Attachment[];
}

export interface TirageInAuction {
  id: string;
  tirage: Tirage;
  auction: Auction;
  tirageDansVentesDescriptionNumeroLot: string;
  tirageDansVentesDescriptionReproduitPage: string;
  tirageDansVentesDescriptionEstimationHaute: string;
  tirageDansVentesDescriptionEstimationBasse: string;
  tirageDansVentesDescriptionVendu: boolean;
  tirageDansVentesDescriptionResultatNonListe: boolean;
  tirageDansVentesDescriptionNonVenu: boolean;
  tirageDansVentesDescriptionNonVendu: boolean;
  tirageDansVentesDescriptionNonReproduit: boolean;
  tirageDansVentesDescriptionRetire: boolean;
  tirageDansVentesDescriptionMiseAPrix: string;
  tirageDansVentesDescriptionPrixMarteau: string;
  tirageDansVentesDescriptionPrixAvecFrais: string;
  tirageDansVentesDescriptionNotes: string;
  attachments?: Attachment[];
}

export interface TirageInBibliography {
  id: string;
  tirage: Tirage;
  bibliography: Bibliography;
  tirageDansBibliographieDescriptionReproduitPage: string;
  tirageDansBibliographieDescriptionCitePage: string;
  tirageDansBibliographieDescriptionPhotographe: string;
  tirageDansBibliographieDescriptionReproductionCouleur: boolean;
  tirageDansBibliographieDescriptionNonPagine: boolean;
  tirageDansBibliographieNotes: string;
  attachments?: Attachment[];
}

export type ArtworkHistory = {
  id: string;
  instanceId: string;
  oeuvresLocalisationHistoriqueHistoriqueNom: string;
  oeuvresLocalisationHistoriqueHistoriqueVille: string;
  oeuvresLocalisationHistoriqueHistoriqueDates: string;
  oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere: boolean;
  artwork?: Artwork;
  updatedAt?: Date;
  createdAt: Date;
};

export interface Ensemble {
  id: string;
  ensemblesDescriptionNomEnsemble: string;
  ensemblesDescriptionType?: string;
  ensemblesDescriptionDateCreationEnsemble?: Date;
  ensemblesDescriptionNotesEnsemble: string;
  visibility?: "private" | "shared";
  artworkCount: number;

  ensemblesText1Document1?: string;
  ensemblesText1Document2?: string;
  ensemblesText2Document1?: string;
  ensemblesText2Document2?: string;
  ensemblesTitreDocument1?: string;
  ensemblesTitreDocument2?: string;
}

// More complete version of Ensemble, that appears in the Settings page
export interface PermanentEnsemble {
  id: string;
  ensemblesDescriptionNomEnsemble: string;
  ensemblesDescriptionType?: string;
  ensemblesDescriptionAuteurEnsemble?: string;
  ensemblesDescriptionOperateurPrincipalEnsemble?: string;
  ensemblesDescriptionOperateurAutre?: string;
  ensemblesDescriptionSignataire?: string;
  ensemblesDescriptionTitreDocumentAdministratif?: string;
  ensemblesDescriptionNotesEnsemble?: string;
  ensemblesCoordonneesAuteurAdresse1?: string;
  ensemblesCoordonneesAuteurAdresse2?: string;
  ensemblesCoordonneesAuteurCodePostal?: string;
  ensemblesCoordonneesAuteurVille?: string;
  ensemblesCoordonneesAuteurPays?: string;
  ensemblesCoordonneesAuteurTelephone?: string;
  ensemblesCoordonneesAuteurMail?: string;
  ensemblesCoordonneesAuteurSiteWeb?: string;
  ensemblesDescriptionRangement?: string;
  ensemblesLegalsText?: string;
  ensemblesTitreDocument1?: string;
  ensemblesText1Document1?: string;
  ensemblesText2Document1?: string;
  ensemblesTitreDocument2?: string;
  ensemblesText1Document2?: string;
  ensemblesText2Document2?: string;
}

export interface LegalPageDTO {
  title: string;
  content: string;
}

export interface Exhibition {
  id: string;
  instanceId: string;
  expositionsDescriptionExpositionPersonnelleDeId: string | null;
  expositionsDescriptionAutresArtistes: string;
  expositionsDescriptionResume: string;
  expositionsPublicationStatus: string;
  sources: string;
  expositionsDescriptionNumeroExpo: string;
  expositionsDescriptionAnnee: string;
  expositionsDescriptionTitre: string;
  expositionsDescriptionTitreDOrigine: string;
  expositionsDescriptionDates: string;
  expositionsDescriptionLieu: string;
  expositionsDescriptionVille: string;
  expositionsDescriptionPays: string;
  expositionsDescriptionTexteSupplementaire: string;
  expositionsDescriptionOrganisateur: string;
  expositionsDescriptionNotes: string;
  expositionsDescriptionExpositionItinerante: boolean;
  expositionsDescriptionExpositionItinerante1EreExpo: string;
  expositionsDescriptionExpositionDansMusee: boolean;
  expositionsDescriptionExpositionCollective: boolean;
  expositionsDescriptionAnneeIncertaine: boolean;
  expositionsDescriptionTitreIncertain: boolean;
  expositionsDescriptionDatesChoixIncertaines: boolean;
  expositionsDescriptionDatesChoixInconnues: boolean;
  expositionsDescriptionLieuIncertain: boolean;
  expositionsDescriptionCityIncertain: boolean;
  expositionsDescriptionCountryIncertain: boolean;
  expositionsDescriptionExpositionPersonnelle: boolean;
  relatedExhibition: Exhibition[];
  from_Exhibition_relatedExhibition: Exhibition[];
  relatedBibliography: Bibliography[];
  relatedAudiovisual: Audiovisual[];
  relatedDocument: Document[];
  relatedWriting: Writing[];
  relatedPhotography: Photography[];
  artworkInExhibitions: ArtworkInExhibition[];
  multipleprintInExhibitions: MultipleprintInExhibition[];
  updatedAt: Date | null;
  createdAt: Date | null;
  updatedById: string | null;
  createdById: string | null;
  attachments: Attachment[];
}

export interface Collector {
  id: string;
  instanceId: string;
  collectionneursDescriptionPrenom: string;
  collectionneursDescriptionNom: string;
  collectionneursDescriptionNomTri: string;
  collectionneursDescriptionVille: string;
  collectionneursDescriptionCivilite: string;
  collectionneursDescriptionLieu: string;
  collectionneursDescriptionRue: string;
  collectionneursDescriptionCodePostal: string;
  collectionneursDescriptionPays: string;
  collectionneursDescriptionCollectionPublique: boolean;
  collectionneursDescriptionNomContact: string;
  collectionneursDescriptionTelephoneMobile: string;
  collectionneursDescriptionTelephone: string;
  collectionneursDescriptionAdresseMail: string;
  collectionneursDescriptionSiteWeb: string;
  collectionneursDescriptionNotes: string;
  collectionneursPublicationStatus: string;
  relatedArtwork: Artwork[];
  relatedTirage: Tirage[];
  collectorInMultipleprints: CollectorInMultipleprint[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
}

export interface Audiovisual {
  id: string;
  instanceId: string;
  audiovisuelDescriptionNumeroEnregistrement: string;
  audiovisuelDescriptionAuteur: string;
  audiovisuelDescriptionAuteurAnonyme: boolean;
  audiovisuelDescriptionTitre: string;
  audiovisuelDescriptionTitreSansTitre: boolean;
  audiovisuelDescriptionTitreInconnu: boolean;
  audiovisuelDescriptionEmission: string;
  audiovisuelDescriptionSousTitre: string;
  audiovisuelDescriptionDateDeCreation: string;
  audiovisuelDescriptionDateDeCreationInconnue: boolean;
  audiovisuelDescriptionDateDe1EreDiffusion: string;
  audiovisuelDescriptionDateDe1EreDiffusionInconnue: boolean;
  audiovisuelDescriptionType: string;
  audiovisuelDescriptionRealisation: string;
  audiovisuelDescriptionProduction: string;
  audiovisuelDescriptionDiffusion: string;
  audiovisuelDescriptionDistribution: string;
  audiovisuelDescriptionPays: string;
  audiovisuelDescriptionResume: string;
  audiovisuelDescriptionTechniqueDuree: string;
  audiovisuelDescriptionTechniqueFormatCineVideo: string;
  audiovisuelDescriptionTechniqueCouleur: boolean;
  audiovisuelDescriptionTechniqueFormatSon: string;
  audiovisuelDescriptionTechniqueVersionLangue: string;
  audiovisuelDescriptionTechniqueSousTitre: string;
  audiovisuelDescriptionTechniqueAssistantRalisation: string;
  audiovisuelDescriptionTechniqueMontage: string;
  audiovisuelDescriptionTechniqueImage: string;
  audiovisuelDescriptionTechniqueMusique: string;
  audiovisuelDescriptionTechniqueSon: string;
  audiovisuelDescriptionTechniqueAutresPostesTechniques: string;
  audiovisuelDescriptionTechniqueActeur: string;
  audiovisuelDescriptionTechniqueRole: string;
  audiovisuelDescriptionNotesDescriptionFilm: string;
  audiovisuelDescriptionNotesDeTravail: string;
  sources: string;
  audiovisuelPublicationStatus: string;
  relatedArtwork: Artwork[];
  relatedExhibition: Exhibition[];
  relatedBibliography: Bibliography[];
  relatedAuction: Auction[];
  relatedDocument: Document[];
  relatedPhotography: Photography[];
  attachments: Attachment[];
  relatedWriting: Writing[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
}

export interface FetchedDocument {
  id: string;
  instanceId: string;
  documentsDescriptionNumeroEnregistrement: string;
  documentsDescriptionTitre: string;
  documentsDescriptionType: string;
  documentsDescriptionClassement: string;
  documentsDescriptionRangement: string;
  documentsDescriptionLocalisation: string;
  documentsDescriptionNombreExemplairesArchives: string;
  documentsDescriptionTheme: string;
  documentsDescriptionDate: string;
  documentsDescriptionDimensions: string;
  documentsDescriptionTechnique: string;
  documentsDescriptionLieuDeCreation: string;
  documentsDescriptionPhotographe: string;
  documentsDescriptionCopyright: string;
  documentsDescriptionCopies: boolean;
  documentsDescriptionNombreFeuillets: string;
  documentsDescriptionPhotoAutresPages: string;
  documentsDescriptionLegendePhotoAutresPages: string;
  documentsDescriptionLegendeRedigee: string;
  documentsDescriptionNotes: string;
  documentsDescriptionRetranscription: string;
  documentsFichierPhotoBasseDefinitionUniquement: boolean;
  documentsFichierPhotoScanAFaire: boolean;
  documentsFichierPhotoOkWeb: boolean;
  documentsFichierPhotoHdOk: boolean;
  documentsValidationPublicationNumerisee: boolean;
  documentsValidationPdfEtOcrOk: boolean;
  documentsAcquisitionDocumentAAcquerir: boolean;
  documentsAcquisitionDocumentAcquis: boolean;
  documentsAcquisitionDateAcquisition: string;
  documentsAcquisitionVendeur: string;
  documentsAcquisitionNotes: string;
  documentsMouvementsDepot: string;
  documentsMouvementsNotesDepot: string;
  documentsMouvementsValeurAssurance: string;
  sources: string;
  documentsPublicationStatus: string;
  relatedArtwork: Artwork[];
  relatedExhibition: Exhibition[];
  relatedAuction: Auction[];
  relatedAudiovisual: Audiovisual[];
  relatedWriting: Writing[];
  documentInBibliography: DocumentInBibliography[];
  attachments: Attachment[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
}

export type Document = Overwrite<
  FetchedDocument,
  {
    documentsDescriptionDimensions?: DimensionsDTO;
  }
>;

export interface FetchedWriting {
  id: string;
  instanceId: string;
  ecritsDescriptionNumeroEnregistrement: string;
  ecritsDescriptionTitre: string;
  ecritsDescriptionType: string;
  ecritsDescriptionClassement: string;
  ecritsDescriptionRangement: string;
  ecritsDescriptionLocalisation: string;
  ecritsDescriptionNombreExemplairesArchives: string;
  ecritsDescriptionTheme: string;
  ecritsDescriptionDate: string;
  ecritsDescriptionDimensions: string;
  ecritsDescriptionTechnique: string;
  ecritsDescriptionLieuDeCreation: string;
  ecritsDescriptionPhotographe: string;
  ecritsDescriptionCopyright: string;
  ecritsDescriptionCopies: boolean;
  ecritsDescriptionNombreFeuillet: string;
  ecritsDescriptionLegendeRedigee: string;
  ecritsDescriptionNotes: string;
  ecritsDescriptionRetranscription: string;
  ecritsFichierPhotoBasseDefinitionUniquement: boolean;
  ecritsFichierPhotoScanAFaire: boolean;
  ecritsFichierPhotoOkWeb: boolean;
  ecritsFichierPhotoHdOk: boolean;
  ecritsValidationPublicationNumerisee: boolean;
  ecritsValidationPdfEtOcrOk: boolean;
  ecritsAcquisitionDocumentAAcquerir: boolean;
  ecritsAcquisitionDocumentAAcquis: boolean;
  ecritsAcquisitionDateAcquisition: string;
  ecritsAcquisitionVendeur: string;
  ecritsAcquisitionNotes: string;
  ecritsMouvementsDepot: string;
  ecritsMouvementsNotesDepot: string;
  ecritsMouvementsValeurAssurance: string;
  ecritsPublicationStatus: string;
  sources: string;
  relatedArtwork: Artwork[];
  relatedExhibition: Exhibition[];
  relatedBibliography?: FetchedBibliography[];
  relatedAuction: Auction[];
  relatedAudiovisual: Audiovisual[];
  relatedDocument?: FetchedDocument[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
  attachments: Attachment[];
}

export type Writing = Overwrite<
  FetchedWriting,
  {
    ecritsDescriptionDimensions?: DimensionsDTO;
    relatedBibliography?: Bibliography[];
    relatedDocument?: Document[];
  }
>;

export interface FetchedPhotography {
  id: string;
  instanceId: string;
  photosDescriptionNumeroEnregistrement: string;
  photosDescriptionType: string;
  photosDescriptionClassement: string;
  photosDescriptionRangement: string;
  photosDescriptionLocalisation: string;
  photosDescriptionNombreExemplairesArchives: string;
  photosDescriptionTitre: string;
  photosDescriptionTheme: string;
  photosDescriptionDate: string;
  photosDescriptionDimensions: string;
  photosDescriptionTechnique: string;
  photosDescriptionLieuDeCreation: string;
  photosDescriptionPhotographe: string;
  photosDescriptionCopyright: string;
  photosDescriptionCopies: boolean;
  photosDescriptionPhoto: string;
  photosDescriptionPhotoDos: string;
  photosDescriptionPhotoVariante: string;
  photosDescriptionPhotoVarianteLegende: string;
  photosDescriptionLegendeRedigee: string;
  photosDescriptionNotes: string;
  photosDescriptionPersonnes: string;
  photosFichierPhotoBasseDefinitionUniquement: boolean;
  photosFichierPhotoScanAFaire: boolean;
  photosFichierPhotoOkWeb: boolean;
  photosFichierPhotoHdOk: boolean;
  photosAcquisitionDocumentAAcquerir: boolean;
  photosAcquisitionDocumentAcquis: boolean;
  photosAcquisitionDateAcquisition: string;
  photosAcquisitionVendeur: string;
  photosAcquisitionNotes: string;
  photosMouvementsDepot: string;
  photosMouvementsNotesDepot: string;
  photosMouvementsValeurAssurance: string;
  photosPublicationStatus: string;
  sources: string;
  relatedArtwork: Artwork[];
  relatedExhibition: Exhibition[];
  relatedAuction: Auction[];
  relatedAudiovisual: Audiovisual[];
  photographyInBibliography: PhotographyInBibliography[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
  attachments: Attachment[];
}

export type Photography = Overwrite<
  FetchedPhotography,
  {
    photosDescriptionDimensions?: DimensionsDTO;
  }
>;

export type TinyPhotography = Pick<
  Photography,
  | "id"
  | "attachments"
  | "photosDescriptionTitre"
  | "photosDescriptionNumeroEnregistrement"
>;
export interface DocumentInBibliography {
  id: string;
  document: Document;
  bibliography: Bibliography;
  documentsDansBibliographieDescriptionReproduitPage: string;
  documentsDansBibliographieDescriptionCitePage: string;
  documentsDansBibliographieDescriptionPhotographe: string;
  documentsDansBibliographieDescriptionReproductionCouleur: boolean;
  documentsDansBibliographieDescriptionNonPagine: boolean;
  attachments?: Attachment[];
}

export interface PhotographyInBibliography {
  id: string;
  photography: Photography;
  bibliography: Bibliography;
  photographiesDansBibliographieDescriptionReproduitPage: string;
  photographiesDansBibliographieDescriptionCitePage: string;
  photographiesDansBibliographieDescriptionPhotographe: string;
  photographiesDansBibliographieDescriptionReproductionCouleur: boolean;
  photographiesDansBibliographieDescriptionNonPagine: boolean;
  attachments?: Attachment[];
}

export interface ModifiableList {
  id: string;
  instanceId: string;
  listKey: string;
  lang: SupportedLocale;
  value: string;
  order: number;
  isLocked?: boolean;
  createdAt: Date | string;
  updatedAt?: Date | string;
}

export interface Trash {
  id?: string;
  documentId?: string;
  documentType?: string;
  title?: string;
  instanceId?: string;
}

export interface Forfait {
  id: string;
  clientName: string;
  email: string;
  maxArtworks: number;
  maxUsers: number;
  maxWeight: number;
  name: string;
  numberArtworks: number;
  numberUsers: number;
  phone: string;
  subscribedOptions: Option[];
  subscriptionStart: Date;
  usedSpaceInGB: number;
  hdOption: boolean;
  users: {
    user: {
      name: string;
      lastName: string;
    };
  }[];
}

export interface Option {
  id: string;
  name: string;
  description: string;
  code: string;
  tooltip: string;
  price: number;
  maxWeight?: number;
  type: string;
}

export type PublicationStatus = "draft" | "published";

export type ArtworkRelation = {
  tasteOf: boolean;
  attributedTo: boolean;
  from: boolean;
  entourageOf: boolean;
  studioOf: boolean;
};

export type TitleState = {
  unknown: boolean;
  none: boolean;
  notConfirmed: boolean;
};

/**
 *
 * @param str This returns undefined, so that zod accepts fields with undefined values.
 * @returns
 */
export const emptyStringToNull = (
  str?: string | null | undefined,
): string | null | undefined => str?.trim() || null;

export const emptyToNullString = (p?: Parameters<typeof z.string>[0]) =>
  z.string(p).optional().nullable().transform(emptyStringToNull);

export type TupleOf<
  T,
  N extends number,
  R extends T[] = [],
> = R["length"] extends N ? R : TupleOf<T, N, [T, ...R]>;

export type ImageSize = {
  width: number;
  height: number;
};

const authorCoreInfoSchema = z.object({
  artistesAuteursDescriptionNom: z.string(),
  artistesAuteursDescriptionPrenom: z.string(),
  artistesAuteursDescriptionDateDeDeces: z.string(),
  artistesAuteursDescriptionDateNaissance: z.string(),
  artistesAuteursDescriptionResumeDates: z.string(),
  artistesAuteursDescriptionPaysDActivite: z.string(),
});

export type AuthorCoreInfo = z.infer<typeof authorCoreInfoSchema>;

const dimensionsSchema = z.object({
  cm_width: z.number(),
  in_width: z.string(),
  cm_height: z.number(),
  in_height: z.string(),
  cm_depth: z.number(),
  in_depth: z.string(),
  cm_volume: z.number(),
  in_volume: z.string(),
  cm_surface: z.number(),
  in_surface: z.string(),
  cm_diameter: z.number(),
  in_diameter: z.string(),
});

export type Dimensions = z.infer<typeof dimensionsSchema>;


export const artworkCoreInfoSchema = z.object({
  imageType: z.literal("artwork"),
  id: z.string(),

  // Title
  oeuvresDescriptionTitre: z.string(),
  oeuvresDescriptionTitreInconnu: z.boolean(),
  oeuvresDescriptionTitreSansTitre: z.boolean(),
  oeuvresDescriptionSousTitre: z.string(),
  oeuvresDescriptionAutreTitre: z.string(),
  oeuvresValidationTitleNotConfirmed: z.boolean(),

  // Technique / Support
  oeuvresDescriptionSupport: multilingualFieldSchema(z.string()),
  oeuvresDescriptionTechnique: multilingualFieldSchema(z.string()),
  oeuvresDescriptionOeuvreDoubleFace: z.boolean(),

  // Dimensions
  oeuvresDescriptionDimensionsOeuvre: dimensionsSchema,
  oeuvresDescriptionDimensionsOeuvreInconnues: z.boolean(),

  // Back Text
  oeuvresDescriptionInscriptionsDosMultilingual: multilingualFieldSchema(
    z.string(),
  ),

  // Signature
  oeuvresDescriptionSignatureLocalisation: multilingualFieldSchema(z.string()),
  oeuvresDescriptionSignatureTranscription: z.string(),

  // Collection
  oeuvresLocalisationHistoriqueCollectionCatalogue: z.string(),
  oeuvresLocalisationHistoriqueCollection: z
    .object({
      collectionneursDescriptionPrenom: z.string(),
      collectionneursDescriptionNom: z.string(),
      collectionneursDescriptionNomTri: z.string(),
      collectionneursDescriptionVille: z.string(),
      collectionneursDescriptionCivilite: z.string(),
      collectionneursDescriptionLieu: z.string(),
      collectionneursDescriptionCollectionPublique: z.boolean(),
    })
    .optional(),

  // Creation Date
  oeuvresDescriptionDateDeCreation: z.string(),
  oeuvresDescriptionDateDeCreationInconnue: z.boolean(),

  // Registration Number
  oeuvresDescriptionNumeroEnregistrement: z.string(),

  // Authors
  relatedAuthors: z.array(authorCoreInfoSchema),

  // Image
  url: z.string(),
});

export type ArtworkCoreInfo = z.infer<typeof artworkCoreInfoSchema>;

export const multipleprintCoreInfoSchema = z.object({
  imageType: z.literal("multipleprint"),
  id: z.string(),

  // Title
  estampesMultiplesDescriptionTitre: z.string(),
  estampesMultiplesDescriptionTitreInconnu: z.boolean(),
  estampesMultiplesDescriptionTitreSansTitre: z.boolean(),
  estampesMultiplesDescriptionSousTitre: z.string(),
  estampesMultiplesDescriptionTitreIncertin: z.boolean(),

  // Technique / Support
  estampesMultiplesDescriptionSupportMateriau: multilingualFieldSchema(
    z.string(),
  ),
  estampesMultiplesDescriptionTechnique: multilingualFieldSchema(z.string()),
  estampesMultiplesDescriptionOeuvreDoubleFace: z.boolean(),

  // Dimensions
  estampesMultiplesDescriptionDimensionsOeuvre: dimensionsSchema,
  estampesMultiplesDescriptionDimensionsOeuvreInconnues: z.boolean(),

  // Back Text
  estampesMultiplesDescriptionInscriptionsDos: multilingualFieldSchema(
    z.string(),
  ),

  // Signature
  estampesMultiplesDescriptionSignatureLocalisation: multilingualFieldSchema(
    z.string(),
  ),
  estampesMultiplesDescriptionSignatureTranscription: z.string(),

  // Creation Date
  estampesMultiplesDescriptionDateDeCreation: z.string(),
  estampesMultiplesDescriptionDateDeCreationInconnue: z.boolean(),

  // Registration Number
  estampesMultiplesDescriptionNumeroEnregistrement: z.string(),

  // Authors
  relatedAuthors: z.array(authorCoreInfoSchema),

  // Image
  url: z.string(),
});

export type MultipleprintCoreInfo = z.infer<typeof multipleprintCoreInfoSchema>;

export const tabMenuItems = [
  "artwork",
  "multipleprint",
  "exhibition",
  "bibliography",
  "auction",
  "author",
  "collector",
  "audiovisual",
  "document",
  "writing",
  "photography",
] as const;

export type TabMenuItems = (typeof tabMenuItems)[number];
